import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  clearLicenses,
  createLicense,
  getAllLicensesByClient,
  updateArbisAiLicense,
} from "../../store/license/actions";
import DefaultTable from "../../components/Common/DefaultTable";
import { useDefaultTable } from "../../helpers/hooks";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import IconButton from "@material-ui/core/IconButton";
import { ApiEndpoint } from "../../store/@core/endpoint";
import { EntityType } from "../../store/@core/entityType";
import {
  downloadFile,
  fetchData,
  postData,
  updateData,
} from "../../helpers/utils";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import EditIcon from "@material-ui/icons/Edit";
import moment from "moment";
import IndeterminateCheckBoxOutlinedIcon from "@material-ui/icons/IndeterminateCheckBoxOutlined";
import { API_URL } from "../../config";
import { Tooltip } from "@material-ui/core";
import CreateLicenseDialog from "./create-license-dialog";
import { Auth } from "../../models/Auth";
import { AUTHORITY } from "../../constants/enums";

const headCells = [
  {
    id: "id",
    label: "ID",
  },
  {
    id: "activatedAt",
    label: "Activated At",
  },
  {
    id: "createdAt",
    label: "Created At",
  },
  {
    id: "expiresAt",
    label: "Expires At",
  },
  {
    id: "lastCheckedAt",
    label: "Last Checked At",
  },
  {
    id: "startsAt",
    label: "Starts At",
  },
  {
    id: "aiEnabled",
    label: "AI Enabled",
  },
  {
    id: "edit",
    label: "",
  },
  {
    id: "terminate",
    label: "",
  },
  {
    id: "download",
    label: "",
  },
];
function LicenseList(props) {
  const {
    context,
    client,
    getAllLicensesByClient,
    clearLicenses,
    updateArbisAiLicense,
  } = props;
  const licenses = context.License.getAll.result;
  const [evolvableLicenses, setEvolvableLicenses] = useState([]);
  const [editLicense, setEditLicense] = useState(null);

  const canManageArbisAi =
    Auth.hasRole(AUTHORITY.ADMIN) ||
    Auth.hasRole(AUTHORITY.LICENSE_ADMIN) ||
    Auth.hasRole(`ROLE_${AUTHORITY.ADMIN}`) ||
    Auth.hasRole(`ROLE_${AUTHORITY.LICENSE_ADMIN}`);

  const { renderHeadCell, renderBodyCell } = useDefaultTable(licenses);

  useEffect(() => {
    if (client) {
      getAllLicensesByClient(client.id);
      fetchData(`${API_URL}/api/clients/${client.id}/evolvable/licenses`).then(
        (res) => {
          setEvolvableLicenses(Array.isArray(res) ? res : []);
        }
      );
    }
    return () => {
      clearLicenses();
    };
  }, [client, getAllLicensesByClient, clearLicenses]);

  const isAiEnabled = (licenseId) => {
    return evolvableLicenses.some(
      (el) => el.uni5LicenseId === licenseId && el.enabled
    );
  };

  const getEvolvableLicense = (licenseId) => {
    return evolvableLicenses.find(
      (el) => el.uni5LicenseId === licenseId && el.enabled
    );
  };

  const handleDownload = (licenseId) => {
    const url = `${ApiEndpoint[EntityType.Client]}/${
      client.id
    }/license/${licenseId}/download`;
    downloadFile(url, `license_${client.name}`, "key").catch((ex) =>
      console.log(ex)
    );
  };

  const handleTerminate = (token) => {
    const url = `${API_URL}/api/licenses/terminate`;
    postData(url, { licenseToken: token })
      .then(() => getAllLicensesByClient(client.id))
      .catch((ex) => console.log(ex));
  };

  const handleEdit = (license) => {
    const evolvable = getEvolvableLicense(license.id);
    const expirationDate = new Date(license.expiresAt);
    const isExpired = !(license.activatedAt && expirationDate > new Date());
    const arbisStartsAt = evolvable?.startsAt
      ? moment(evolvable.startsAt).format("YYYY-MM-DDTHH:mm")
      : "";
    const arbisExpiresAt = evolvable?.expiresAt
      ? moment(evolvable.expiresAt).format("YYYY-MM-DDTHH:mm")
      : "";
    setEditLicense({
      ...license,
      arbisAiEnabled: Boolean(evolvable),
      arbisAiStartsAt: arbisStartsAt,
      arbisAiExpiresAt: arbisExpiresAt,
      evolvableLicenseId: evolvable?.id || null,
      isExpired,
    });
  };

  const handleEditClose = () => {
    setEditLicense(null);
  };

  const refreshEvolvableLicenses = () => {
    fetchData(`${API_URL}/api/clients/${client.id}/evolvable/licenses`).then(
      (res) => {
        setEvolvableLicenses(Array.isArray(res) ? res : []);
      }
    );
  };

  const handleUpdateArbisAi = (form) => {
    const evolvable = getEvolvableLicense(form.id);
    if (form.arbisAiEnabled && !evolvable) {
      // Enabling Arbis AI on an existing license — POST new evolvable license
      const arbisUrl = `${API_URL}/api/evolvable/license`;
      postData(arbisUrl, {
        clientId: parseInt(client.id),
        uni5LicenseId: form.id,
        startsAt: form.arbisAiStartsAt,
        expiresAt: form.arbisAiExpiresAt,
      })
        .then(() => refreshEvolvableLicenses())
        .catch((ex) => console.log(ex));
    } else if (form.arbisAiEnabled && evolvable) {
      // Arbis AI already enabled — update dates via PUT
      const arbisUrl = `${API_URL}/api/evolvable/license/${evolvable.id}`;
      updateData(arbisUrl, {
        clientId: parseInt(client.id),
        uni5LicenseId: form.id,
        startsAt: form.arbisAiStartsAt,
        expiresAt: form.arbisAiExpiresAt,
      })
        .then(() => refreshEvolvableLicenses())
        .catch((ex) => console.log(ex));
    } else if (!form.arbisAiEnabled && evolvable) {
      // Disabling Arbis AI — PUT remove
      updateArbisAiLicense({
        licenseId: evolvable.id,
        clientId: client.id,
        arbisAiEnabled: false,
      });
      setTimeout(() => refreshEvolvableLicenses(), 1000);
    }
    setEditLicense(null);
  };

  const currentDate = new Date();
  const renderDefaultBody = () => {
    return licenses.map((license) => {
      const expirationDate = new Date(license.expiresAt);
      const isActive = license.activatedAt && expirationDate > currentDate;
      return (
        <TableRow hover role="checkbox" tabIndex={-1} key={license.id}>
          {renderBodyCell(license.id)}
          {renderBodyCell(moment(license.activatedAt).format("lll"))}
          {renderBodyCell(moment(license.createdAt).format("lll"))}
          {renderBodyCell(moment(license.expiresAt).format("lll"))}
          {renderBodyCell(moment(license.lastCheckedAt).format("lll"))}
          {renderBodyCell(moment(license.startsAt).format("lll"))}
          {renderBodyCell(isAiEnabled(license.id) ? "Yes" : "No")}
          <TableCell align="right">
            {isActive && (
              <Tooltip title={"Edit license"}>
                <IconButton
                  aria-label="edit"
                  size="small"
                  onClick={() => handleEdit(license)}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
          </TableCell>
          <TableCell align="right">
            <Tooltip title={"Terminate license"}>
              <IconButton
                aria-label="options"
                size="small"
                onClick={() => handleTerminate(license.token)}
                disabled={!isActive}
              >
                <IndeterminateCheckBoxOutlinedIcon />
              </IconButton>
            </Tooltip>
          </TableCell>
          <TableCell align="right">
            <Tooltip title={"Download license"}>
              <IconButton
                aria-label="options"
                size="small"
                onClick={() => handleDownload(license.id)}
              >
                <CloudDownloadIcon />
              </IconButton>
            </Tooltip>
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <>
      <DefaultTable
        headCells={headCells}
        rows={licenses}
        renderHeadCell={renderHeadCell}
        renderDefaultBody={renderDefaultBody}
      />
      {editLicense && (
        <CreateLicenseDialog
          open={Boolean(editLicense)}
          handleClose={handleEditClose}
          context={context.License}
          entityType={EntityType.License}
          clientId={client?.id}
          editLicense={editLicense}
          updateEntity={handleUpdateArbisAi}
        />
      )}
    </>
  );
}
const mapStateToProps = (state) => {
  return {
    context: state,
  };
};

export default connect(mapStateToProps, {
  clearLicenses,
  getAllLicensesByClient,
  createLicense,
  updateArbisAiLicense,
})(LicenseList);
